import React, { useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { FaPlus } from "react-icons/fa";
import { FiEdit2, FiTrash2, FiFileText, FiFolder, FiRefreshCw } from "react-icons/fi";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import SearchSelect from "components/ui/SearchSelect";
import Table from "components/ui/Table";
import Toggle from "components/ui/Toggle";
import Tooltip from "components/ui/Tooltip";
import CircularProgress from "components/ui/CircularProgress";
import { formatDateTime } from "utils/helperFunctions";
import {
  useGetKnowledgeDocumentsQuery,
  useGetKnowledgeDocumentQuery,
  useAddKnowledgeDocumentMutation,
  useUpdateKnowledgeDocumentMutation,
  useReprocessKnowledgeDocumentMutation,
  useDeleteKnowledgeDocumentMutation,
  useSearchKnowledgeMutation,
} from "apis/knowledge";
import { useGetNetworksQuery } from "apis/network";

const ACCEPT = ".pdf,.docx,.txt,.md,.jpg,.jpeg,.png,.webp,.mp4,.mov,.webm,.mp3,.m4a,.wav,.zip";

const KIND_LABEL = {
  pdf: "PDF",
  word: "Word",
  text: "Text",
  image: "Picture",
  video: "Video",
  audio: "Recording",
  zip: "Zip",
};

const STATUS_STYLE = {
  processing: "bg-yellow-100 text-yellow-800",
  ready: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

const STATUS_LABEL = { processing: "Reading…", ready: "Ready", failed: "Failed" };

const STEP_LABEL = {
  downloading: "Getting the file",
  extracting: "Taking the files out",
  uploading: "Sending to Gemini",
  preparing: "Gemini is preparing it",
  reading: "Reading it",
  indexing: "Making it searchable",
};

// Where a document being read is: its step and how far through it, or waiting its turn (files are read one at a time).
const ReadingProgress = ({ document }) => {
  const { progress } = document;
  if (!progress?.step) return <p className="mt-1 text-xs text-darkgray">Waiting its turn</p>;
  return (
    <div className="mt-1 w-36">
      <p className="text-xs text-darkgray">
        {STEP_LABEL[progress.step] || progress.step}
        {progress.percent != null && ` · ${progress.percent}%`}
      </p>
      {progress.percent != null && (
        <div className="mt-1 h-1 w-full rounded bg-lightgray">
          <div className="h-1 rounded bg-black transition-all" style={{ width: `${progress.percent}%` }} />
        </div>
      )}
    </div>
  );
};

const inputClass = (error) => `form-input border ${error ? "border-red-500" : "border-mediumgray"}`;

const ActionIcon = ({ label, disabled, onClick, children }) => (
  <Tooltip
    tooltip={label}
    text={
      <button
        className={`view-icon ${disabled ? "pointer-events-none opacity-40" : ""}`}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </button>
    }
  />
);

const sizeOf = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const optionOf = (network) => ({ value: network._id, label: network.name });

// ─── Add / Edit modal ─────────────────────────────────────────────────────────
const DocumentModal = ({ isOpen, onClose, document, networks }) => {
  const isEdit = !!document;
  const [progress, setProgress] = useState(null);
  const add = useAddKnowledgeDocumentMutation();
  const update = useUpdateKnowledgeDocumentMutation();
  const saving = add.isPending || update.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm({ defaultValues: { file: null, title: "", area: "", networks: [] } });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      file: null,
      title: document?.title ?? "",
      area: document?.area ?? "",
      networks: (document?.networks || []).map(optionOf),
    });
    setProgress(null);
  }, [isOpen, document, reset]);

  const chosenFile = watch("file")?.[0];

  const submitForm = ({ file: files, title, area, networks: chosen }) => {
    const file = files?.[0];
    const onProgress = file ? setProgress : undefined;
    const done = { onSuccess: onClose, onSettled: () => setProgress(null) };
    const networkIds = chosen.map((option) => option.value);
    if (isEdit) update.mutate({ id: document._id, title, area, networks: networkIds, file, onProgress }, done);
    else add.mutate({ file, title: title || file.name, area, networks: networkIds, onProgress }, done);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={isEdit ? "Edit Document" : "Add Document"}
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="file" className="form-label">
            {isEdit ? "Replace the file" : "File *"}
          </label>
          <input
            type="file"
            id="file"
            accept={ACCEPT}
            {...register("file", { validate: (files) => isEdit || !!files?.length || "Choose a file" })}
            className="block w-full text-sm text-darkergray file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-lightgray file:text-darkergray hover:file:bg-mediumgray cursor-pointer"
            disabled={saving}
          />
          {errors.file && <p className="form-error-message">{errors.file.message}</p>}
          <p className="text-sm text-darkgray">
            PDF or Word up to 50 MB · text or Markdown up to 20 MB · JPG, PNG or WebP up to 20 MB · MP3, M4A or WAV up
            to 1 GB · MP4, MOV or WebM up to 2 GB · a zip of these up to 2 GB, each file added on its own
          </p>
          {isEdit && chosenFile && (
            <p className="text-sm text-darkgray">The current file is deleted and this one is read instead.</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="title" className="form-label">
            Title {isEdit && "*"}
          </label>
          <input
            type="text"
            id="title"
            {...register("title", {
              validate: (value) => !isEdit || !!value.trim() || "Title is required",
            })}
            className={inputClass(errors.title)}
            placeholder={chosenFile?.name || "What the document is called"}
            disabled={saving}
          />
          {errors.title && <p className="form-error-message">{errors.title.message}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="area" className="form-label">
            Area
          </label>
          <input
            type="text"
            id="area"
            {...register("area")}
            className={inputClass(errors.area)}
            placeholder="e.g. Open homes, Compliance, Listing presentation"
            disabled={saving}
          />
        </div>

        <div className="space-y-2">
          <label className="form-label">
            Networks
          </label>
          <Controller
            name="networks"
            control={control}
            render={({ field }) => (
              <SearchSelect
                value={field.value}
                onChange={field.onChange}
                multiple
                options={networks.map(optionOf)}
                placeholder="All networks"
                disabled={saving}
              />
            )}
          />
          <p className="text-sm text-darkgray">
            Pilot answers only these networks' agents from it. Leave empty for every network.
            {document?.kind === "zip" && " The files taken out of this zip change with it."}
          </p>
        </div>

        {progress !== null && (
          <div className="flex items-center gap-3">
            <CircularProgress value={progress} />
            <p className="text-sm text-darkgray">{progress < 100 ? "Uploading…" : "Uploaded, adding…"}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-mediumgray">
          <Button className="outline-button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" className="black-button" loading={saving}>
            {isEdit ? "Save Changes" : "Add"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ─── What was read from the file ──────────────────────────────────────────────
const ReadTextModal = ({ id, onClose }) => {
  const { data, isPending } = useGetKnowledgeDocumentQuery(id);
  return (
    <Modal isOpen={!!id} onClose={onClose} title={data?.title || "Document"} className="max-w-3xl">
      {isPending ? (
        <p className="text-center text-sm text-darkgray">Loading…</p>
      ) : data?.text ? (
        <>
          <p className="text-sm text-darkgray">What Pilot reads from this file, in {data.sections} sections.</p>
          <pre className="whitespace-pre-wrap break-words rounded bg-lightgray p-4 text-xs text-darkergray">
            {data.text}
          </pre>
        </>
      ) : (
        <p className="text-center text-sm text-darkgray">
          {data?.status === "processing" ? "Still being read." : "Nothing has been read from this file."}
        </p>
      )}
    </Modal>
  );
};

// ─── What a zip held ──────────────────────────────────────────────────────────
const ZipContentsModal = ({ zip, files, onClose }) => (
  <Modal isOpen={!!zip} onClose={onClose} title={zip?.title || "Zip"} className="max-w-3xl">
    {zip?.status === "processing" ? (
      <p className="text-center text-sm text-darkgray">Still taking the files out of this zip.</p>
    ) : (
      <>
        <div className="space-y-2">
          <h5>Added ({files.length})</h5>
          {files.length === 0 && <p className="text-sm text-darkgray">No file in this zip could be added.</p>}
          {files.map((file) => (
            <div key={file._id} className="flex items-start justify-between gap-4 border-b border-mediumgray py-2">
              <div>
                <p className="text-sm text-darkergray">{file.file?.name}</p>
                {file.status === "failed" && file.error && <p className="form-error-message">{file.error}</p>}
              </div>
              <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${STATUS_STYLE[file.status] || ""}`}>
                {STATUS_LABEL[file.status] || file.status}
              </span>
            </div>
          ))}
        </div>
        {zip?.skipped?.length > 0 && (
          <div className="space-y-2">
            <h5>Couldn't be added ({zip.skipped.length})</h5>
            {zip.skipped.map((file, index) => (
              <div key={index} className="border-b border-mediumgray py-2">
                <p className="text-sm text-darkergray">{file.name}</p>
                <p className="form-error-message">{file.reason}</p>
              </div>
            ))}
          </div>
        )}
      </>
    )}
  </Modal>
);

const DeleteModal = ({ document, fileCount, onClose }) => {
  const remove = useDeleteKnowledgeDocumentMutation();
  return (
    <Modal isOpen={!!document} onClose={onClose} title="Delete Document" className="max-w-sm">
      <p className="text-sm text-darkergray">
        Delete <strong>{document?.title}</strong>
        {fileCount > 0 && ` and the ${fileCount} files taken out of it`}? Pilot stops using{" "}
        {fileCount > 0 ? "them" : "it"} straight away, and the {fileCount > 0 ? "files are" : "file is"} deleted.
      </p>
      <div className="flex justify-end gap-2 pt-4">
        <Button className="outline-button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="black-button"
          loading={remove.isPending}
          onClick={() => remove.mutate(document._id, { onSuccess: onClose })}
        >
          Delete
        </Button>
      </div>
    </Modal>
  );
};

// ─── Try a question ───────────────────────────────────────────────────────────
const ALL_DOCUMENTS = { value: "", label: "Every document" };

const SearchPanel = ({ networks }) => {
  const search = useSearchKnowledgeMutation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ defaultValues: { question: "", network: ALL_DOCUMENTS } });

  const submitForm = ({ question, network }) => search.mutate({ question: question.trim(), networkId: network.value });

  return (
    <div className="mb-6 rounded border border-mediumgray p-4">
      <h5 className="mb-1">Try a question</h5>
      <p className="mb-3 text-sm text-darkgray">
        Ask the way an agent would, to see what Pilot finds in these documents. Pick a network to see what its agents
        get.
      </p>
      <form onSubmit={handleSubmit(submitForm)} className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            {...register("question", { validate: (value) => !!value.trim() || "Type a question" })}
            className={`${inputClass(errors.question)} flex-1`}
            placeholder="e.g. What do I do if a buyer asks for the contract at an open home?"
          />
          <div className="w-56">
            <Controller
              name="network"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={[ALL_DOCUMENTS, ...networks.map(optionOf)]}
                />
              )}
            />
          </div>
          <Button type="submit" className="black-button" loading={search.isPending}>
            Search
          </Button>
        </div>
        {errors.question && <p className="form-error-message">{errors.question.message}</p>}
      </form>

      {search.data && (
        <div className="mt-4 space-y-3">
          {search.data.length === 0 && (
            <p className="text-sm text-darkgray">Nothing close enough. Pilot would answer without these documents.</p>
          )}
          {search.data.map((result, index) => (
            <div key={index} className="rounded bg-lightgray p-3">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-darkgray">
                <span className="font-semibold text-darkergray">{result.document}</span>
                {result.section && <span>› {result.section}</span>}
                {result.where && <span>· {result.where}</span>}
                <span className="ml-auto">{Math.round(result.score * 100)}% match</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-darkergray">
                {result.text.length > 600 ? `${result.text.slice(0, 600)}…` : result.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const pageSize = 20;

const PilotKnowledge = () => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [readingId, setReadingId] = useState(null);
  const [openZip, setOpenZip] = useState(null);

  const { data, isPending } = useGetKnowledgeDocumentsQuery();
  const { data: networks } = useGetNetworksQuery();
  const update = useUpdateKnowledgeDocumentMutation();
  const reprocess = useReprocessKnowledgeDocumentMutation();

  const titleOf = useMemo(() => new Map(data.map((document) => [document._id, document.title])), [data]);
  const filesOf = (zip) => data.filter((document) => document.fromZip === zip?._id);
  // Why a document can't be read again right now, or null.
  const readAgainBlockedBy = (document) => {
    if (document.status === "processing") return "Being read now";
    if (filesOf(document).some((file) => file.status === "processing"))
      return "Its files are still being read";
    return null;
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Document",
        isFilter: true,
        minWidth: "220px",
        cell: (row) => (
          <div>
            <p className="font-medium text-darkergray">{row.title}</p>
            <p className="text-xs text-darkgray">
              {row.file?.name} · {sizeOf(row.file?.size || 0)}
            </p>
            {row.fromZip && <p className="text-xs text-darkgray">From {titleOf.get(row.fromZip) || "a zip"}</p>}
          </div>
        ),
      },
      {
        accessorKey: "area",
        header: "Area",
        isFilter: true,
        cell: (row) => row.area || "—",
      },
      {
        accessorKey: "networks",
        header: "Networks",
        isFilter: false,
        isSortable: false,
        cell: (row) =>
          row.networks?.length ? row.networks.map((network) => network.name).join(", ") : "All networks",
      },
      {
        accessorKey: "kind",
        header: "Type",
        isFilter: true,
        cell: (row) => KIND_LABEL[row.kind] || row.kind,
      },
      {
        accessorKey: "status",
        header: "Status",
        isFilter: true,
        minWidth: "160px",
        cell: (row) => (
          <div>
            <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[row.status] || ""}`}>
              {STATUS_LABEL[row.status] || row.status}
            </span>
            {row.status === "processing" && <ReadingProgress document={row} />}
            {row.kind === "zip" && row.status !== "processing" ? (
              <>
                <p className="mt-1 text-xs text-darkgray">{filesOf(row).length} files added</p>
                {row.skipped?.length > 0 && (
                  <p className="form-error-message">{row.skipped.length} couldn't be added</p>
                )}
              </>
            ) : (
              row.status === "ready" && <p className="mt-1 text-xs text-darkgray">{row.sections} sections</p>
            )}
            {row.status === "failed" && row.error && <p className="form-error-message">{row.error}</p>}
          </div>
        ),
      },
      {
        accessorKey: "isActive",
        header: "Pilot uses it",
        isFilter: false,
        stopPropagation: true,
        cell: (row) => (
          <Toggle checked={row.isActive} onChange={(isActive) => update.mutate({ id: row._id, isActive })} />
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Added",
        isFilter: false,
        minWidth: "160px",
        cell: (row) => (
          <div className="text-xs">
            <p>{formatDateTime(row.createdAt)}</p>
            {row.addedBy?.name && <p className="text-darkgray">{row.addedBy.name}</p>}
          </div>
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        cell: (row) => (
          <div className="flex items-center gap-3">
            {row.kind === "zip" ? (
              <ActionIcon label="Files in this zip" onClick={() => setOpenZip(row)}>
                <FiFolder size={16} />
              </ActionIcon>
            ) : (
              <ActionIcon label="What Pilot reads" onClick={() => setReadingId(row._id)}>
                <FiFileText size={16} />
              </ActionIcon>
            )}
            <ActionIcon label="Edit or replace the file" onClick={() => setEditing(row)}>
              <FiEdit2 size={16} />
            </ActionIcon>
            <ActionIcon
              label={readAgainBlockedBy(row) || (row.kind === "zip" ? "Take the files out again" : "Read the file again")}
              disabled={!!readAgainBlockedBy(row)}
              onClick={() => reprocess.mutate(row._id)}
            >
              <FiRefreshCw size={16} />
            </ActionIcon>
            <ActionIcon label="Delete" onClick={() => setDeleting(row)}>
              <FiTrash2 size={16} />
            </ActionIcon>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const AddButton = () => (
    <Button className="black-button flex items-center gap-2" onClick={() => setIsAddOpen(true)}>
      <FaPlus size={14} />
      Add Document
    </Button>
  );

  if (isPending) return <PageLoader />;

  return (
    <>
      <DocumentModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} networks={networks} />
      <DocumentModal isOpen={!!editing} onClose={() => setEditing(null)} document={editing} networks={networks} />
      <DeleteModal document={deleting} fileCount={filesOf(deleting).length} onClose={() => setDeleting(null)} />
      <ReadTextModal id={readingId} onClose={() => setReadingId(null)} />
      <ZipContentsModal
        zip={openZip && data.find((document) => document._id === openZip._id)}
        files={filesOf(openZip)}
        onClose={() => setOpenZip(null)}
      />

      <div className="container mx-auto">
        <p className="mb-4 text-sm text-darkgray">
          Procedures, policies, scripts, checklists and training Pilot answers from, for every network or only the ones
          you pick. Videos and recordings are transcribed, and pictures and scanned pages are read.
        </p>
        <SearchPanel networks={networks} />
        <Table
          title="Pilot Knowledge"
          columns={columns}
          data={data}
          pageSize={pageSize}
          ButtonCmp={AddButton}
          emptyText="No documents yet."
        />
      </div>
    </>
  );
};

export default PilotKnowledge;
